import { authEndpoints, getAudience } from "../config";
import { UserClaims } from "../types";
import { tokenStore } from "./tokenStore";

// One-time cleanup of tokens persisted by older builds (pre-RCPLATFORM-416),
// which stored them in localStorage under these keys.
const LEGACY_LOCALSTORAGE_AUTH_KEYS = ["bri_token", "bri_refresh_token", "bri_user_info", "bri_verify_user_cache_v1"];
LEGACY_LOCALSTORAGE_AUTH_KEYS.forEach((key) => localStorage.removeItem(key));

class AuthService {
  private sessionVersion = 0;
  private pendingRefresh: { version: number; promise: Promise<string | null> } | null = null;
  private refreshListeners = new Set<(token: string) => void>();

  onRefreshed(listener: (token: string) => void): () => void {
    this.refreshListeners.add(listener);
    return () => { this.refreshListeners.delete(listener); };
  }

  /** Last successful `/verify` claims for this access token (speeds repeat visits). */
  getCachedUserClaims(token: string): UserClaims | null {
    return tokenStore.getCachedClaims(token);
  }

  private setCachedUserClaims(token: string, claims: UserClaims): void {
    tokenStore.setCachedClaims(token, claims);
  }

  getToken(): string | null {
    return tokenStore.getToken();
  }

  setToken(token: string): void {
    this.sessionVersion += 1;
    tokenStore.setToken(token);
  }

  setRefreshToken(token: string): void {
    tokenStore.setRefreshToken(token);
  }

  clearAuth(): void {
    this.sessionVersion += 1;
    tokenStore.clear();
  }

  redirectToLogin(): void {
    const uri = `${window.location.origin}/auth/callback`;
    const params = new URLSearchParams({
      redirect_uri: uri,
      audience: getAudience()
    });
    window.location.href = `${authEndpoints.loginUrl}?${params.toString()}`;
  }

  async getUserInfo(): Promise<UserClaims | null> {
    const token = this.getToken();
    if (!token) {
      return null;
    }

    try {
      const response = await fetch(authEndpoints.verifyUrl, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        return null;
      }

      const body = await response.json();
      if (this.getToken() !== token) return null;
      const claims = body.claims || null;
      if (claims) {
        this.setCachedUserClaims(token, claims);
      }
      return claims;
    } catch {
      return this.getCachedUserClaims(token);
    }
  }

  async refreshToken(rejectedToken?: string | null): Promise<string | null> {
    const token = this.getToken();
    if (!token) {
      return null;
    }
    // A delayed 401 may arrive after another request already renewed this token.
    if (rejectedToken && rejectedToken !== token) return token;
    const version = this.sessionVersion;
    if (this.pendingRefresh?.version === version) return this.pendingRefresh.promise;
    const promise = this.performRefresh(token, version);
    this.pendingRefresh = { version, promise };
    try {
      return await promise;
    } finally {
      if (this.pendingRefresh?.promise === promise) this.pendingRefresh = null;
    }
  }

  private async performRefresh(token: string, version: number): Promise<string | null> {
    const isCurrent = () => this.sessionVersion === version && this.getToken() === token;
    const claims = this.getCachedUserClaims(token);

    try {
      const response = await fetch(authEndpoints.refreshUrl, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!isCurrent()) return null;
      if (!response.ok) {
        this.clearAuth();
        return null;
      }

      const body = await response.json();
      if (!isCurrent()) return null;
      if (typeof body.accessToken !== "string" || !body.accessToken.trim()) {
        this.clearAuth();
        return null;
      }

      this.setToken(body.accessToken);
      if (claims) this.setCachedUserClaims(body.accessToken, claims);
      this.refreshListeners.forEach((listener) => listener(body.accessToken));
      return body.accessToken;
    } catch {
      if (isCurrent()) this.clearAuth();
      return null;
    }
  }
}

export const authService = new AuthService();
